import { QueryKey } from '@tanstack/react-query';
import { stringify } from 'qs';

import {
  Annotation,
  AnnotationDraftVersionResponse,
  AnnotationExtension,
  AnnotationVersion,
  AnnotationWorkingStates,
  BulkAnnotationWorkingStateRequest,
  BulkAnnotationWorkingStateResponse,
} from 'interfaces/annotation';
import { UndoStates } from 'services/annotations/annotationEditHistoryStore';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';

export const getSlideAnnotations = ({
  slideId,
  includeEmpty,
  signal,
}: {
  slideId: string;
  includeEmpty?: boolean;
  signal?: AbortSignal;
}) => {
  const stringifiedParams = stringify({ includeEmpty });
  return apiRequestHandlerPromise<Annotation[]>({
    url: `slides/${slideId}/annotation${stringifiedParams ? `?${stringifiedParams}` : ''}`,
    method: 'GET',
    signal,
  });
};

export const getSlideAnnotationsQueryKey = ({
  slideId,
  includeEmpty,
}: {
  slideId: string;
  includeEmpty?: boolean;
}): QueryKey => ['annotations', { slideId, includeEmpty }];

export const saveAnnotationDraft = ({
  annotationAssignmentId,
  slideId,
  annotationsData,
  history,
  signal,
}: {
  annotationAssignmentId: number;
  slideId: string;
  annotationsData?: AnnotationExtension['extensionAnnotationsData'];
  history?: UndoStates;
  signal?: AbortSignal;
}) => {
  return apiRequestHandlerPromise<AnnotationDraftVersionResponse>({
    url: `annotation_assignments/${annotationAssignmentId}/slides/${slideId}/draft.update`,
    method: 'POST',
    data: JSON.stringify({
      extensionAnnotationsData: annotationsData,
      history,
    }),
    signal,
  });
};

export const prepareAnnotationDraft = ({
  annotationAssignmentId,
  slideId,
  signal,
}: {
  annotationAssignmentId: number;
  slideId: string;
  signal?: AbortSignal;
}) => {
  return apiRequestHandlerPromise<AnnotationDraftVersionResponse>({
    url: `annotation_assignments/${annotationAssignmentId}/slides/${slideId}/draft.prepare`,
    method: 'POST',
    signal,
  });
};

export const changeAnnotationWorkingState = ({
  annotationAssignmentId,
  slideId,
  workingState,
  draftVersionHash,
  allowEmptyDraftPublish,
}: {
  annotationAssignmentId: number;
  slideId: string;
  workingState: AnnotationWorkingStates;
  draftVersionHash?: string;
  allowEmptyDraftPublish?: boolean;
}) => {
  return apiRequestHandlerPromise<void>({
    url: `annotation_assignments/${annotationAssignmentId}/slides/${slideId}/working_state.update`,
    method: 'POST',
    data: JSON.stringify({
      workingState,
      ...(draftVersionHash ? { draftVersionHash } : {}),
      ...(allowEmptyDraftPublish ? { allowEmptyDraftPublish } : {}),
    }),
  });
};

export const bulkChangeAnnotationWorkingState = ({
  annotationAssignmentId,
  request,
}: {
  annotationAssignmentId: number;
  request: BulkAnnotationWorkingStateRequest;
}) => {
  return apiRequestHandlerPromise<BulkAnnotationWorkingStateResponse>({
    url: `annotation_assignments/${annotationAssignmentId}/slides/working_state.update`,
    method: 'POST',
    data: JSON.stringify(request),
  });
};

export const publishAnnotationVersion = ({
  annotationAssignmentId,
  slideId,
}: {
  annotationAssignmentId: number;
  slideId: string;
}) => {
  return apiRequestHandlerPromise<void>({
    url: `annotation_assignments/${annotationAssignmentId}/slides/${slideId}/publish.execute`,
    method: 'POST',
  });
};

export const getAnnotationVersionHistory = ({
  annotationAssignmentId,
  slideId,
  signal,
}: {
  annotationAssignmentId: number;
  slideId: string;
  signal?: AbortSignal;
}) => {
  return apiRequestHandlerPromise<AnnotationVersion[]>({
    url: `annotation_assignments/${annotationAssignmentId}/slides/${slideId}/version_history.get`,
    method: 'GET',
    signal,
  });
};

export const getAnnotationVersionHistoryQueryKey = ({
  annotationAssignmentId,
  slideId,
}: {
  annotationAssignmentId?: number | null;
  slideId: string;
}): QueryKey => ['annotationVersionHistory', { slideId, annotationAssignmentId }];

export const getAnnotationVersion = ({
  annotationAssignmentId,
  slideId,
  annotationVersionId,
  signal,
}: {
  annotationAssignmentId: number;
  slideId: string;
  annotationVersionId: number;
  signal?: AbortSignal;
}) => {
  return apiRequestHandlerPromise<AnnotationVersion>({
    url: `annotation_assignments/${annotationAssignmentId}/slides/${slideId}/version_history/${annotationVersionId}.get`,
    method: 'GET',
    signal,
  });
};

export const getAnnotationVersionQueryKey = ({
  annotationAssignmentId,
  slideId,
  annotationVersionId,
}: {
  annotationAssignmentId?: number | null;
  slideId: string;
  annotationVersionId?: number | null;
}): QueryKey => ['annotationVersion', { slideId, annotationAssignmentId, annotationVersionId }];

export const restoreAnnotationVersionToDraft = ({
  annotationAssignmentId,
  slideId,
  annotationVersionId,
}: {
  annotationAssignmentId: number;
  slideId: string;
  annotationVersionId: number;
}) => {
  return apiRequestHandlerPromise<AnnotationDraftVersionResponse>({
    url: `annotation_assignments/${annotationAssignmentId}/slides/${slideId}/version_history/${annotationVersionId}/restore_to_draft.execute`,
    method: 'POST',
  });
};
